import type { ReactNode } from "react";
import { Loader2Icon, TagIcon, WalletIcon, XCircleIcon, CheckIcon, SettingsIcon } from "lucide-react";
import type { ServiceKind } from "@woodpecker/protocol";
import { Badge, Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { Tooltip, TooltipContent, TooltipTrigger } from "../ui/overlay";
import { cn } from "../lib/cn";
import { useT, useTT } from "../i18n";
import { ServiceIcon } from "../lib/icons";
import { batchText, maxOf, priceText, round2 } from "../lib/format";
import type { ServiceEntry } from "../adapters/chain";
import { useStore } from "../store/store";

const KIND_CLASS: Record<ServiceKind, string> = {
  runner: "bg-sky-500/15 text-sky-700 dark:text-sky-300",
  memory: "bg-violet-500/15 text-violet-700 dark:text-violet-300",
  host: "bg-orange-500/15 text-orange-700 dark:text-orange-300",
  model: "",
  tool: "",
  store: "bg-sky-100 text-sky-800 dark:bg-sky-950 dark:text-sky-200",
};
const KIND_TIP: Partial<Record<ServiceKind, string>> = {
  memory: "kind.memoryTip",
  host: "kind.hostTip",
  store: "kind.storeTip",
};

export function KindBadge({ kind }: { kind: ServiceKind }) {
  const t = useT();
  const tip = KIND_TIP[kind];
  const badge = (
    <Badge
      variant={kind === "model" ? "default" : kind === "tool" ? "outline" : "secondary"}
      className={KIND_CLASS[kind]}
      tabIndex={tip ? 0 : undefined}
    >
      {t(`kind.${kind}`)}
    </Badge>
  );
  return tip ? (
    <Tooltip>
      <TooltipTrigger asChild>{badge}</TooltipTrigger>
      <TooltipContent>{t(tip)}</TooltipContent>
    </Tooltip>
  ) : (
    badge
  );
}

export function Kinds({ kinds, className }: { kinds: ServiceKind[]; className?: string }) {
  return (
    <div className={cn("flex gap-1", className)}>
      {kinds.map((k) => (
        <KindBadge key={k} kind={k} />
      ))}
    </div>
  );
}

export function MineBadge({ entry }: { entry: ServiceEntry }) {
  const t = useT();
  return entry.mine ? (
    <Badge variant="outline" className="h-4 px-1.5 text-[10px] font-normal text-emerald-700 dark:text-emerald-300">
      {t("common.mine")}
    </Badge>
  ) : null;
}

export function manageHref(name: string): string {
  return `#/services/${encodeURIComponent(name)}`;
}

export function ServiceName({ entry, className }: { entry: ServiceEntry; className?: string }) {
  const i = entry.name.indexOf("/") + 1;
  return (
    <div className={cn("flex items-start gap-2 font-mono text-sm", className)}>
      <ServiceIcon entry={entry} />
      <span className="min-w-0">
        <span className="whitespace-nowrap">{entry.name.slice(0, i)}</span>
        <wbr />
        <span className="inline-block max-w-full wrap-anywhere">{entry.name.slice(i)}</span>
      </span>
    </div>
  );
}

export function ManageButton({ entry }: { entry: ServiceEntry }) {
  const t = useT();
  return entry.mine ? (
    <Button size="sm" variant="outline" asChild>
      <a href={manageHref(entry.name)}>
        <SettingsIcon />
        {t("common.manage")}
      </a>
    </Button>
  ) : null;
}

export function LabelTag({ children }: { children: ReactNode }) {
  return (
    <span className="inline-flex h-4 max-w-full items-center gap-1 rounded-sm bg-muted px-1.5 text-[10px] text-muted-foreground">
      <TagIcon className="size-2.5 shrink-0" />
      <span className="truncate">{children}</span>
    </span>
  );
}

export function Labels({ entry }: { entry: ServiceEntry }) {
  return (
    <>
      {entry.labels.map((l, i) => (
        <LabelTag key={i}>{l}</LabelTag>
      ))}
    </>
  );
}

/** "fixed · 1 tLLM per call · ≈ 1 wp" */
export function PriceLine({ entry, className }: { entry: ServiceEntry; className?: string }) {
  const t = useT();
  const tt = useTT();
  const { wpPerToken } = useStore();
  return (
    <span className={cn("font-mono text-xs", className)}>
      {tt(priceText(entry.price, entry.token))}{" "}
      <span className="text-muted-foreground">
        · {t("common.wp", { n: round2(maxOf(entry.price) * wpPerToken(entry.token)) })}
      </span>
    </span>
  );
}

export function BatchLine({ entry }: { entry: ServiceEntry }) {
  const tt = useTT();
  return <span className="font-mono text-xs text-muted-foreground">· {tt(batchText(entry.batch))}</span>;
}

/** "1 wpCredit = 4 tGpt" with an optional Buy link. */
export function RateLine({ token, buy = false, className }: { token: string; buy?: boolean; className?: string }) {
  const t = useT();
  const { tokens, directory } = useStore();
  const { onBuy } = useBuy();
  if (token === "wpCredit") return null;
  const meta = tokens.get(token);
  const per = meta && meta.rate > 0n ? Math.round((1e6 / Number(meta.rate)) * 100) / 100 : 0;
  const canBuy = buy && directory.some((e) => e.token === token);
  return (
    <span
      className={cn("inline-flex flex-wrap items-center gap-x-1.5 font-mono text-xs text-muted-foreground", className)}
    >
      {t("rate.line", { n: per, token })}
      {canBuy && (
        <Button variant="link" size="sm" className="h-auto p-0 font-sans text-xs" onClick={() => onBuy(token)}>
          {t("rate.buy")}
        </Button>
      )}
    </span>
  );
}

import { createContext, useContext } from "react";
export const BuyContext = createContext<{ buying: string | null; onBuy: (token: string | null) => void }>({
  buying: null,
  onBuy: () => undefined,
});
export function useBuy() {
  return useContext(BuyContext);
}

export function StatusDot({ status }: { status: string }) {
  const t = useT();
  return (
    <span className="inline-flex items-center gap-1.5 text-sm">
      <span
        className={cn(
          "size-2 rounded-full",
          status === "running"
            ? "animate-pulse bg-amber-500"
            : status === "paused"
              ? "bg-amber-500"
              : status === "stopped"
                ? "bg-destructive"
                : status === "done"
                  ? "bg-emerald-500"
                  : "bg-muted-foreground/40",
        )}
      />
      {t(`status.${status}`)}
    </span>
  );
}

export function Waiting({ n, message, className }: { n: number; message?: boolean; className?: string }) {
  const t = useT();
  return (
    <p className={cn("inline-flex items-center gap-2 text-sm text-muted-foreground", className)}>
      <Loader2Icon className="size-4 shrink-0 animate-spin" />
      {message ? t("wallet.waitingMessage") : n === 1 ? t("wallet.waiting1") : t("wallet.waitingN", { n })}
    </p>
  );
}

export function Rejected({ className }: { className?: string }) {
  const t = useT();
  return (
    <p className={cn("inline-flex items-center gap-2 text-sm text-destructive", className)}>
      <XCircleIcon className="size-4 shrink-0" />
      {t("wallet.rejected")}
    </p>
  );
}

export function SignedOut({ what }: { what: string }) {
  const t = useT();
  return (
    <Card className="border-dashed shadow-none">
      <CardContent className="flex flex-col items-center gap-2 py-14 text-center">
        <WalletIcon className="size-6 text-muted-foreground" />
        <p className="font-medium">{t("signedOut.title")}</p>
        <p className="max-w-sm text-sm text-muted-foreground">{t(what)}</p>
        <p className="max-w-sm text-xs text-muted-foreground">{t("signedOut.hint")}</p>
      </CardContent>
    </Card>
  );
}

export function Check({ ok }: { ok: boolean }) {
  return ok ? <CheckIcon className="size-3.5" /> : null;
}

export function SectionLabel({ children }: { children: ReactNode }) {
  return <div className="text-[11px] font-medium tracking-wide text-muted-foreground uppercase">{children}</div>;
}
